import React, { FC, useState } from 'react';
import { TextField, Box, Typography, useTheme, Tabs, Tab, Grid, Switch } from '@mui/material';

import { Account, BuilderComponentProps, UploadOptions, useCanUpgrade, useNode } from '@mnz/builder';
import { boxCss, cardContentCss, cardCss, cardMediaCss, descriptionCss, labelCss } from '../../common/style';
import { px2rem } from '../../core';
import TabPanel from '../../common/components/tab-panel/TabPanel';
import ImageCard from './ImageCard';
import ImageDetailModal from './ImageSettingModal';
import UploadCard from './UploadCard';
import PlanUpgradeButton from '../../common/components/button/PlanUpgradeButton';
import StyledComponent from '../../core/StyledComponent';
import { Lock } from '@menuzen/icon/build/solid';

export interface ImageGalleryProps extends BuilderComponentProps {
    uploadImage: (file: File, options?: Partial<UploadOptions>) => Promise<string>;
    title: string;
    showTitle: boolean;
    images: ImageProps[];
    upgradePlan: () => void;
}

export interface ImageProps {
    url: string;
    caption: string;
}

export interface SelectedImage extends ImageProps {
    idx: number;
}

const bgGradient = 'linear-gradient(90deg, #F4FCFC 0%, #F9EEFB 100%);';
const borderGradient = 'linear-gradient(90deg, #AAE8E9 0%, #E6BBED 100%);';

export const ImageGallerySetting: FC<ImageGalleryProps> = ({ uploadImage, upgradePlan }) => {
    const {
        actions: { setProp },
        props: { images, title, showTitle, account },
    } = useNode((node) => {
        return {
            hidden: node.data.hidden,
            props: node.data.props as unknown as ImageGalleryProps,
        };
    });
    const imageLimits = 4;
    const theme = useTheme();
    const [tab, setTab] = useState('images');
    const [openImageDetailModal, setOpenImageDetailModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState<null | SelectedImage>(null);
    const [message, setMessage] = useState<null | string>(null);
    const handleAddImage = (urls: string[]) => {
        const incomingImages = urls.map((url) => ({ url, caption: '' }));
        const newImages = incomingImages.concat(images);
        setProp<ImageGalleryProps>((props) => {
            props.images = newImages;
        });
    };
    const handleRemoveImage = (idx: number) => {
        setProp<ImageGalleryProps>((props) => props.images.splice(idx, 1));
    };
    const handleChangeImageCaption = (caption: string, idx: number) => {
        const newImages = [...images];
        newImages[idx] = { ...newImages[idx], caption };
        setProp<ImageGalleryProps>((props) => {
            props.images = newImages;
        });
    };
    const canUpgrade = useCanUpgrade({ account: account as Account });
    return (
        <>
            <Box mt={2} mb={3}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                    <Tab label="Images" value={`images`} />
                    <Tab label="Settings" value={`settings`} />
                </Tabs>
            </Box>
            <Box mb={12}>
                <TabPanel currentTab={tab} value="images">
                    <Box
                        sx={{
                            marginTop: px2rem(10),
                            padding: images.length === 0 ? 0 : px2rem(10),
                            borderRadius: images.length === 0 ? 0 : px2rem(5),
                            backgroundColor: images.length === 0 ? 'none' : theme.palette.primary10,
                        }}
                    >
                        <Grid container spacing={1}>
                            <UploadCard
                                onSuccess={(url) => handleAddImage(url)}
                                uploadImage={uploadImage}
                                disabled={canUpgrade && images.length >= imageLimits}
                                setMessage={setMessage}
                                canUpgrade={canUpgrade}
                                isFirstUpload={images.length === 0}
                            />
                            {images &&
                                images.length > 0 &&
                                images.map((image, idx) => (
                                    <Grid key={image.url} item xs={4}>
                                        <ImageCard
                                            onSelect={() => {
                                                setOpenImageDetailModal(true);
                                                setSelectedImage({ ...image, idx });
                                            }}
                                            url={image.url}
                                            onRemove={() => handleRemoveImage(idx)}
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                    </Box>
                    {canUpgrade && images.length >= 4 && (
                        <Typography
                            sx={{
                                marginTop: px2rem(5),
                                fontSize: px2rem(10),
                                color: theme.palette.error.main,
                                textAlign: 'center',
                            }}
                        >
                            You have uploaded the maximum of four images.
                        </Typography>
                    )}
                    {message && (
                        <Typography
                            sx={{
                                marginTop: px2rem(5),
                                fontSize: px2rem(10),
                                color: theme.palette.error.main,
                                textAlign: 'center',
                            }}
                        >
                            {message}
                        </Typography>
                    )}
                    {canUpgrade && (
                        <StyledComponent
                            xs={{
                                borderRadius: px2rem(6),
                                padding: px2rem(2),
                                backgroundImage: borderGradient,
                                marginTop: px2rem(10),
                            }}
                        >
                            <StyledComponent
                                xs={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderRadius: px2rem(6),
                                    padding: px2rem(12),
                                    backgroundImage: bgGradient,
                                    cursor: 'pointer',
                                }}
                                onClick={upgradePlan}
                            >
                                <StyledComponent>
                                    <StyledComponent
                                        xs={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: px2rem(12),
                                        }}
                                    >
                                        <Lock />
                                        <Typography
                                            sx={{
                                                marginLeft: px2rem(2),
                                                fontSize: px2rem(12),
                                                lineHeight: '120%',
                                                fontWeight: 700,
                                            }}
                                        >
                                            Add Unlimited Images
                                        </Typography>
                                    </StyledComponent>
                                    <Typography sx={{ fontSize: px2rem(10), lineHeight: '120%', marginTop: px2rem(2) }}>
                                        Upgrade to Grow to upload unlimited images
                                    </Typography>
                                </StyledComponent>
                                <StyledComponent xs={{ flexShrink: 0 }}>
                                    <PlanUpgradeButton btnText="Upgrade" />
                                </StyledComponent>
                            </StyledComponent>
                        </StyledComponent>
                    )}
                </TabPanel>
                <TabPanel currentTab={tab} value="settings">
                    <Box sx={[boxCss(theme)]}>
                        <Box css={cardCss()}>
                            <Box sx={cardContentCss()}>
                                <Typography sx={labelCss()}>Gallery Title</Typography>
                                <Typography sx={descriptionCss(theme)}>Maximum 80 characters</Typography>
                            </Box>

                            <Box css={cardMediaCss()}>
                                <Switch
                                    checked={showTitle}
                                    onChange={(_, checked) =>
                                        setProp<ImageGalleryProps>((props) => (props.showTitle = checked))
                                    }
                                />
                            </Box>
                        </Box>
                        <TextField
                            sx={{ marginTop: px2rem(12) }}
                            value={title}
                            multiline
                            rows={3}
                            fullWidth={true}
                            inputProps={{ maxLength: 80 }}
                            onChange={(e) => setProp<ImageGalleryProps>((props) => (props.title = e.target.value))}
                        />
                    </Box>
                </TabPanel>
            </Box>
            <ImageDetailModal
                idx={selectedImage ? selectedImage.idx : -1}
                url={selectedImage ? images[selectedImage.idx].url : ''}
                caption={selectedImage ? images[selectedImage.idx].caption : ''}
                open={openImageDetailModal}
                handleChangeImageCaption={handleChangeImageCaption}
                onBack={() => {
                    setOpenImageDetailModal(false);
                    setSelectedImage(null);
                }}
                onClose={() => {
                    setOpenImageDetailModal(false);
                    setSelectedImage(null);
                }}
            />
        </>
    );
};
